<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('employees.title')" :description="t('employees.description')">
      <template #actions>
        <AppButton variant="outline" size="sm" @click="handleImport">
          <Upload class="h-4 w-4" /> {{ t('common.upload') }}
        </AppButton>
        <AppButton variant="outline" size="sm" @click="exportEmployees">
          <Download class="h-4 w-4" /> {{ t('common.export') }}
        </AppButton>
        <AppButton size="sm" @click="openAddModal">
          <Plus class="h-4 w-4" /> {{ t('employees.addEmployee') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Stats -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <AppStatCard :label="t('employees.totalEmployees')" :value="employees.length"    :icon="Users"     iconBg="bg-blue-50"    iconColor="text-blue-600" />
      <AppStatCard :label="t('common.active')"           :value="activeEmployees"     :icon="UserCheck" iconBg="bg-emerald-50" iconColor="text-emerald-600" />
      <AppStatCard :label="t('common.inactive')"         :value="inactiveEmployees"   :icon="UserX"     iconBg="bg-red-50"     iconColor="text-red-600" />
      <AppStatCard :label="t('nav.departments')"         :value="departmentsList.length" :icon="Building" iconBg="bg-purple-50" iconColor="text-purple-600" />
    </div>

    <!-- Filters -->
    <AppCard class="p-4">
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('common.search') }}</label>
          <AppInput v-model="filters.search" :placeholder="t('employees.searchEmployees')">
            <template #prefix><Search class="h-3.5 w-3.5" /></template>
          </AppInput>
        </div>
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('common.department') }}</label>
          <AppSelect v-model="filters.department">
            <option value="">{{ t('employees.filterByDepartment') }}</option>
            <option v-for="dept in departmentsList" :key="dept.id" :value="dept.name">{{ dept.name }}</option>
          </AppSelect>
        </div>
        <div>
          <label class="block text-xs font-medium text-muted-foreground mb-1">{{ t('common.status') }}</label>
          <AppSelect v-model="filters.status">
            <option value="">{{ t('employees.filterByStatus') }}</option>
            <option value="active">{{ t('common.active') }}</option>
            <option value="inactive">{{ t('common.inactive') }}</option>
          </AppSelect>
        </div>
      </div>
    </AppCard>

    <!-- Table -->
    <AppCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <p class="text-sm font-medium text-foreground">Employees ({{ filteredEmployees.length }})</p>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12 gap-3 text-muted-foreground">
        <AppSpinner />
        <span class="text-sm">Loading employees…</span>
      </div>

      <AppEmptyState
        v-else-if="paginatedEmployees.length === 0"
        :icon="Users"
        :title="t('employees.noEmployees')"
        description="Get started by adding your first employee."
      >
        <template #action>
          <AppButton size="sm" @click="openAddModal"><Plus class="h-4 w-4" /> {{ t('employees.addEmployee') }}</AppButton>
        </template>
      </AppEmptyState>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-muted/50 border-b border-border">
              <th class="px-4 py-3 text-left font-medium text-muted-foreground">{{ t('employees.name') }}</th>
              <th class="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">Contact</th>
              <th class="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell">{{ t('employees.department') }}</th>
              <th class="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell">{{ t('employees.position') }}</th>
              <th class="px-4 py-3 text-left font-medium text-muted-foreground">{{ t('employees.status') }}</th>
              <th class="px-4 py-3 text-right font-medium text-muted-foreground">{{ t('employees.actions') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border">
            <tr v-for="employee in paginatedEmployees" :key="employee.id" class="hover:bg-muted/20 transition-colors">
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <UserAvatar :name="employee.name" size="md" />
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-foreground truncate">{{ employee.name }}</p>
                    <p class="text-xs text-muted-foreground">{{ employee.employee_id }}</p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 hidden md:table-cell">
                <p class="text-sm text-foreground">{{ employee.email }}</p>
                <p class="text-xs text-muted-foreground">{{ employee.phone || 'N/A' }}</p>
              </td>
              <td class="px-4 py-3 text-sm text-foreground hidden lg:table-cell">{{ employee.department }}</td>
              <td class="px-4 py-3 text-sm text-foreground hidden lg:table-cell">{{ employee.position }}</td>
              <td class="px-4 py-3">
                <AppBadge :variant="employee.is_active ? 'success' : 'destructive'">
                  {{ employee.is_active ? 'Active' : 'Inactive' }}
                </AppBadge>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center justify-end gap-1">
                  <AppButton variant="ghost" size="icon-sm" title="View" @click="viewEmployee(employee)">
                    <Eye class="h-3.5 w-3.5" />
                  </AppButton>
                  <AppButton variant="ghost" size="icon-sm" title="Edit" @click="editEmployee(employee)">
                    <Edit class="h-3.5 w-3.5" />
                  </AppButton>
                  <AppButton variant="ghost" size="icon-sm" :title="employee.is_active ? 'Deactivate' : 'Activate'" @click="toggleEmployeeStatus(employee)">
                    <UserCheck v-if="!employee.is_active" class="h-3.5 w-3.5 text-emerald-600" />
                    <UserX v-else class="h-3.5 w-3.5 text-amber-600" />
                  </AppButton>
                  <AppButton variant="ghost" size="icon-sm" title="Reset Password" @click="resetEmployeePassword(employee)">
                    <Key class="h-3.5 w-3.5" />
                  </AppButton>
                  <AppButton variant="ghost" size="icon-sm" class="text-destructive hover:bg-red-50" title="Delete" @click="wipeEmployee(employee)">
                    <Trash2 class="h-3.5 w-3.5" />
                  </AppButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
        <p class="text-xs text-muted-foreground">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredEmployees.length) }} of {{ filteredEmployees.length }}
        </p>
        <div class="flex items-center gap-1">
          <AppButton variant="outline" size="icon-sm" :disabled="currentPage === 1" @click="currentPage--">
            <ChevronLeft class="h-4 w-4" />
          </AppButton>
          <button
            v-for="page in visiblePages"
            :key="page"
            :class="['h-7 min-w-[28px] px-2 rounded-md text-xs font-medium transition-colors', page === currentPage ? 'bg-primary text-primary-foreground' : typeof page === 'number' ? 'hover:bg-accent text-foreground' : 'text-muted-foreground cursor-default']"
            @click="typeof page === 'number' && (currentPage = page)"
          >{{ page }}</button>
          <AppButton variant="outline" size="icon-sm" :disabled="currentPage === totalPages" @click="currentPage++">
            <ChevronRight class="h-4 w-4" />
          </AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Add/Edit Employee Modal -->
    <Teleport to="body">
      <div
        v-if="showAddModal || showEditModal"
        class="fixed inset-0 bg-gradient-to-br from-gray-900/50 to-gray-600/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-10 px-4"
        @mousedown="handleBackdropMouseDown"
        @click="handleBackdropClick"
      >
        <div
          ref="modalContent"
          class="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl m-4 animate-modal-slide-in overflow-hidden"
          @click.stop
          @mousedown.stop
        >
          <!-- Modal Header with Gradient -->
          <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
            <div class="flex items-start justify-between">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <User class="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 class="text-xl font-semibold text-white">
                    {{ showAddModal ? t('employees.addEmployee') : t('employees.editEmployee') }}
                  </h3>
                  <p class="mt-1 text-sm text-blue-100">
                    {{ showAddModal ? t('employees.addEmployeeDesc') : t('employees.editEmployeeDesc') }}
                  </p>
                </div>
              </div>
              <button
                @click="closeModal"
                :disabled="saving"
                class="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="Close modal (ESC)"
              >
                <X class="h-5 w-5" />
              </button>
            </div>
          </div>

          <!-- Modal Body with Form -->
          <form @submit.prevent="saveEmployee" class="p-6 space-y-6">

            <!-- Section 1: Basic Information -->
            <div class="space-y-4">
              <div class="flex items-center space-x-2 mb-4">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <User class="h-4 w-4 text-blue-600" />
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Basic Information</h4>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Employee ID -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Hash class="h-4 w-4 mr-1.5 text-gray-500" />
                      Employee ID
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <div class="relative">
                    <input
                      v-model="form.employee_id"
                      type="text"
                      ref="firstInput"
                      autofocus
                      :class="[
                        'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                        'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                        'placeholder:text-gray-400',
                        hasError('employee_id')
                          ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white',
                      ]"
                      placeholder="e.g. EMP001"
                      @input="validateOnInput('employee_id', form.employee_id)"
                    />
                  </div>
                  <p v-if="hasError('employee_id')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("employee_id") }}
                  </p>
                </div>

                <!-- Full Name -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <User class="h-4 w-4 mr-1.5 text-gray-500" />
                      Full Name
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <input
                    v-model="form.name"
                    type="text"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400',
                      hasError('name')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    placeholder="Enter full name"
                    @input="validateOnInput('name', form.name)"
                  />
                  <p v-if="hasError('name')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("name") }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Section 2: Contact Details -->
            <div class="space-y-4 pt-4 border-t border-gray-100">
              <div class="flex items-center space-x-2 mb-4">
                <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Mail class="h-4 w-4 text-green-600" />
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Contact Details</h4>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Email -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Mail class="h-4 w-4 mr-1.5 text-gray-500" />
                      Email Address
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <input
                    v-model="form.email"
                    type="email"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400',
                      hasError('email')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    placeholder="employee@example.com"
                    @input="validateOnInput('email', form.email)"
                  />
                  <p v-if="hasError('email')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("email") }}
                  </p>
                  <p v-else class="mt-1.5 text-xs text-gray-500">Used for login and notifications</p>
                </div>

                <!-- Phone -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Phone class="h-4 w-4 mr-1.5 text-gray-500" />
                      Phone Number
                    </span>
                  </label>
                  <input
                    v-model="form.phone"
                    type="tel"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400',
                      hasError('phone')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    placeholder="+1 (555) 000-0000"
                    @input="validateOnInput('phone', form.phone)"
                  />
                  <p v-if="hasError('phone')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("phone") }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Section 3: Employment Details -->
            <div class="space-y-4 pt-4 border-t border-gray-100">
              <div class="flex items-center space-x-2 mb-4">
                <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Briefcase class="h-4 w-4 text-purple-600" />
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Employment Details</h4>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Department -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Building class="h-4 w-4 mr-1.5 text-gray-500" />
                      Department
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <select
                    v-model="form.department"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      hasError('department')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    @change="validateOnInput('department', form.department)"
                  >
                    <option value="">Select Department</option>
                    <option v-for="dept in departmentsList" :key="dept.id" :value="dept.id">
                      {{ dept.name }}
                    </option>
                  </select>
                  <p v-if="hasError('department')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("department") }}
                  </p>
                </div>

                <!-- Position -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Briefcase class="h-4 w-4 mr-1.5 text-gray-500" />
                      Position
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <input
                    v-model="form.position"
                    type="text"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400',
                      hasError('position')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    placeholder="e.g. Senior Developer"
                    @input="validateOnInput('position', form.position)"
                  />
                  <p v-if="hasError('position')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("position") }}
                  </p>
                </div>

                <!-- Hire Date -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Calendar class="h-4 w-4 mr-1.5 text-gray-500" />
                      Hire Date
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <input
                    v-model="form.hire_date"
                    type="date"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      hasError('hire_date')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    @change="validateOnInput('hire_date', form.hire_date)"
                  />
                  <p v-if="hasError('hire_date')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("hire_date") }}
                  </p>
                </div>

                <!-- Work Schedule -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Clock class="h-4 w-4 mr-1.5 text-gray-500" />
                      Work Schedule
                    </span>
                  </label>
                  <select
                    v-model="form.work_schedule"
                    class="w-full pl-4 pr-4 py-2.5 rounded-lg border-2 border-gray-200 hover:border-gray-300 bg-white transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="office">🏢 Office Based</option>
                    <option value="field">🚗 Field Work</option>
                    <option value="hybrid">🔄 Hybrid</option>
                  </select>
                </div>

                <!-- Manager -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <User class="h-4 w-4 mr-1.5 text-gray-500" />
                      Manager
                    </span>
                  </label>
                  <select
                    v-model="form.manager_id"
                    class="w-full pl-4 pr-4 py-2.5 rounded-lg border-2 border-gray-200 hover:border-gray-300 bg-white transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="">No Manager</option>
                    <option v-for="mgr in managerOptions" :key="mgr.id" :value="mgr.id">
                      {{ mgr.name }}{{ mgr.role === 'admin' ? ' (Admin)' : mgr.position ? ` — ${mgr.position}` : '' }}
                    </option>
                  </select>
                </div>

                <!-- Salary -->
                <div class="md:col-span-2">
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <span class="h-4 w-4 mr-1.5 text-gray-500">💰</span>
                      Salary (Optional)
                    </span>
                  </label>
                  <div class="relative">
                    <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">{{ getCurrencySymbol() }}</span>
                    <input
                      v-model="form.salary"
                      type="number"
                      step="0.01"
                      min="0"
                      :class="[
                        'w-full pl-8 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                        'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                        'placeholder:text-gray-400',
                        hasError('salary')
                          ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white',
                      ]"
                      placeholder="0.00"
                      @input="validateOnInput('salary', form.salary)"
                    />
                  </div>
                  <p v-if="hasError('salary')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("salary") }}
                  </p>
                  <p v-else class="mt-1.5 text-xs text-gray-500">Leave empty if not applicable</p>
                </div>
              </div>
            </div>

            <!-- Section 4: Security (Password for new employees) -->
            <div v-if="showAddModal" class="space-y-4 pt-4 border-t border-gray-100">
              <div class="flex items-center space-x-2 mb-4">
                <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Key class="h-4 w-4 text-amber-600" />
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Security</h4>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Password -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Key class="h-4 w-4 mr-1.5 text-gray-500" />
                      Password
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <input
                    v-model="form.password"
                    type="password"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400',
                      hasError('password')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    placeholder="••••••••"
                    @input="validateOnInput('password', form.password)"
                  />
                  <p v-if="hasError('password')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("password") }}
                  </p>
                  <p v-else class="mt-1.5 text-xs text-gray-500">Minimum 6 characters</p>
                </div>

                <!-- Confirm Password -->
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    <span class="flex items-center">
                      <Key class="h-4 w-4 mr-1.5 text-gray-500" />
                      Confirm Password
                      <span class="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                  <input
                    v-model="form.confirmPassword"
                    type="password"
                    :class="[
                      'w-full pl-4 pr-4 py-2.5 rounded-lg border-2 transition-all duration-200',
                      'focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                      'placeholder:text-gray-400',
                      hasError('confirmPassword')
                        ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    ]"
                    placeholder="••••••••"
                    @input="validateOnInput('confirmPassword', form.confirmPassword, { password: form.password })"
                  />
                  <p v-if="hasError('confirmPassword')" class="mt-1.5 text-sm text-red-600 flex items-center">
                    <span class="mr-1">⚠</span> {{ getError("confirmPassword") }}
                  </p>
                  <p v-else class="mt-1.5 text-xs text-gray-500">Must match password</p>
                </div>
              </div>
            </div>

            <!-- Form Actions -->
            <div class="sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-6 mt-6">
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <!-- Help Text -->
                  <div class="flex items-start space-x-2 text-sm">
                    <div class="flex-shrink-0 mt-0.5">
                      <div class="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                        <span class="text-xs text-blue-600">ℹ</span>
                      </div>
                    </div>
                    <div>
                      <p class="text-gray-700 font-medium">Quick Tips</p>
                      <p class="text-gray-500 text-xs mt-0.5">
                        <span class="text-red-500">*</span> Required fields must be filled • Press
                        <kbd class="px-1.5 py-0.5 text-xs font-semibold bg-white border border-gray-300 rounded shadow-sm">ESC</kbd>
                        to close
                      </p>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex items-center gap-3">
                    <button
                      type="button"
                      @click="closeModal"
                      :disabled="saving"
                      class="px-5 py-2.5 border-2 border-gray-300 rounded-lg shadow-sm bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95"
                    >
                      <span class="flex items-center">
                        <X class="h-4 w-4 mr-1.5" />
                        Cancel
                      </span>
                    </button>
                    <button
                      type="submit"
                      :disabled="saving || hasErrors"
                      class="relative px-6 py-2.5 border-2 border-transparent rounded-lg shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 text-sm font-semibold text-white hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-xl"
                    >
                      <span v-if="saving" class="inline-flex items-center">
                        <svg
                          class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          ></circle>
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {{ showAddModal ? "Creating Employee..." : "Updating Employee..." }}
                      </span>
                      <span v-else class="flex items-center">
                        <UserCheck class="h-4 w-4 mr-1.5" />
                        {{ showAddModal ? "Create Employee" : "Update Employee" }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- View Employee Modal -->
    <Teleport to="body">
      <div
        v-if="showViewModal && selectedEmployee"
        class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
        @click="closeViewModal"
      >
        <div
          class="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white"
          @click.stop
        >
          <div class="mt-3">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-lg font-medium text-gray-900">
                Employee Details
              </h3>
              <button
                @click="closeViewModal"
                class="text-gray-400 hover:text-gray-600"
              >
                <X class="h-6 w-6" />
              </button>
            </div>

            <div class="space-y-6">
              <!-- Profile Header -->
              <div class="flex items-center space-x-4">
                <div class="flex-shrink-0">
                  <UserAvatar
                    :name="selectedEmployee.name"
                    size="xl"
                  />
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-gray-900">
                    {{ selectedEmployee.name }}
                  </h4>
                  <p class="text-sm text-gray-500">
                    {{ selectedEmployee.employee_id }}
                  </p>
                  <span
                    :class="[
                      selectedEmployee.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800',
                      'mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                    ]"
                  >
                    {{ selectedEmployee.is_active ? "Active" : "Inactive" }}
                  </span>
                </div>
              </div>

              <!-- Employee Information -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 class="text-sm font-medium text-gray-500 mb-3">
                    Contact Information
                  </h5>
                  <div class="space-y-2">
                    <div class="flex items-center">
                      <Mail class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.email
                      }}</span>
                    </div>
                    <div class="flex items-center">
                      <Phone class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.phone || "Not provided"
                      }}</span>
                    </div>
                    <div class="flex items-start">
                      <MapPin class="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span class="text-sm text-gray-900 break-words">{{
                        selectedEmployee.address || "Not provided"
                      }}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 class="text-sm font-medium text-gray-500 mb-3">
                    Work Information
                  </h5>
                  <div class="space-y-2">
                    <div class="flex items-center">
                      <Building class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.department
                      }}</span>
                    </div>
                    <div class="flex items-center">
                      <Briefcase class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.position
                      }}</span>
                    </div>
                    <div class="flex items-center">
                      <User class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.manager_name || "No manager assigned"
                      }}</span>
                    </div>
                    <div class="flex items-center">
                      <Calendar class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.work_schedule
                      }}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 class="text-sm font-medium text-gray-500 mb-3">
                    Employment Details
                  </h5>
                  <div class="space-y-2">
                    <div>
                      <p class="text-sm font-medium text-gray-500">Hire Date</p>
                      <p class="text-sm text-gray-900">
                        {{ formatDate(selectedEmployee.hire_date) }}
                      </p>
                    </div>
                    <div v-if="selectedEmployee.salary">
                      <p class="text-sm font-medium text-gray-500">Salary</p>
                      <p class="text-sm text-gray-900">
                        {{ formatCurrency(selectedEmployee.salary) }}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 class="text-sm font-medium text-gray-500 mb-3">
                    Account Information
                  </h5>
                  <div class="space-y-2">
                    <div>
                      <p class="text-sm font-medium text-gray-500">Created</p>
                      <p class="text-sm text-gray-900">
                        {{ formatDate(selectedEmployee.created_at) }}
                      </p>
                    </div>
                    <div>
                      <p class="text-sm font-medium text-gray-500">
                        Last Updated
                      </p>
                      <p class="text-sm text-gray-900">
                        {{ formatDate(selectedEmployee.updated_at) }}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 class="text-sm font-medium text-gray-500 mb-3">
                    Emergency Contact
                  </h5>
                  <div class="space-y-2">
                    <div class="flex items-center">
                      <User class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.emergency_contact_name || "Not provided"
                      }}</span>
                    </div>
                    <div class="flex items-center">
                      <Phone class="h-4 w-4 text-gray-400 mr-2" />
                      <span class="text-sm text-gray-900">{{
                        selectedEmployee.emergency_contact_phone || "Not provided"
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div
                class="flex justify-end space-x-3 pt-6 border-t border-gray-200"
              >
                <button
                  @click="editEmployee(selectedEmployee)"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Edit class="h-4 w-4 mr-2" />
                  Edit Employee
                </button>
                <button
                  @click="resetEmployeePassword(selectedEmployee)"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Key class="h-4 w-4 mr-2" />
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- File Input for Import (Hidden) -->
    <input
      ref="fileInput"
      type="file"
      accept=".xlsx,.xls"
      @change="handleFileSelect"
      class="hidden"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { adminService } from "@/services/adminService";
import { useEmployeeValidation } from "@/composables/useEmployeeValidation";
import { exportEmployeesToExcel, parseExcelFile } from "@/utils/exportUtils";
import { toast } from "@/composables/useToast";
import { useCurrency } from "@/composables/useCurrency";

// Icons from lucide-vue-next
import {
  Plus,
  Search,
  Download,
  Upload,
  Users,
  UserCheck,
  UserX,
  Building,
  Eye,
  Edit,
  Key,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  X,
  ChevronLeft,
  ChevronRight,
  Hash,
  Clock,
  Trash2,
} from "lucide-vue-next";
import UserAvatar from "@/components/common/UserAvatar.vue";
import { AppPageHeader, AppCard, AppStatCard, AppBadge, AppButton, AppInput, AppSelect, AppEmptyState, AppSpinner } from "@/components/ui";

const router = useRouter();
const { t } = useI18n();
const { formatCurrency, getCurrencySymbol, loadCurrency } = useCurrency();

// Validation composable
const {
  errors,
  hasErrors,
  validateForm,
  validateOnInput,
  getError,
  hasError,
  clearErrors,
  setSubmitting,
} = useEmployeeValidation();

// State
const employees = ref([]);
const loading = ref(true);
const saving = ref(false);
const showAddModal = ref(false);
const showEditModal = ref(false);
const showViewModal = ref(false);
const selectedEmployee = ref(null);
const fileInput = ref(null);
const modalContent = ref(null);
const firstInput = ref(null);
const backdropMouseDownTarget = ref(null);

// Filters
const filters = ref({
  search: "",
  department: "",
  status: "",
});

// Pagination
const currentPage = ref(1);
const itemsPerPage = 10;

// Form data
const form = ref({
  employee_id: "",
  name: "",
  email: "",
  phone: "",
  department: "",
  position: "",
  salary: "",
  hire_date: "",
  work_schedule: "office",
  manager_id: "",
  password: "",
  confirmPassword: "",
});

// Manager candidates (all active employees + admins)
const managersList = ref([]);

const managerOptions = computed(() => {
  // An employee cannot be their own manager
  const excludeId = showEditModal.value ? selectedEmployee.value?.id : null;
  return managersList.value.filter((m) => m.id !== excludeId);
});

// Departments (fetched from backend)
const departmentsList = ref([]);
const departments = computed(() => {
  return [...new Set(employees.value?.map(emp => emp.department).filter(dept => dept && dept !== 'N/A'))].sort();
});

// Computed properties
const filteredEmployees = computed(() => {
  let filtered = employees.value;

  if (filters.value.search) {
    const searchTerm = filters.value.search.toLowerCase();
    filtered = filtered.filter(
      (emp) =>
        emp.name.toLowerCase().includes(searchTerm) ||
        emp.email.toLowerCase().includes(searchTerm) ||
        emp.employee_id.toLowerCase().includes(searchTerm)
    );
  }

  if (filters.value.department) {
    filtered = filtered.filter(
      (emp) => emp.department === filters.value.department
    );
  }

  if (filters.value.status) {
    const isActive = filters.value.status === "active";
    filtered = filtered.filter((emp) => emp.is_active === isActive);
  }

  return filtered;
});

const paginatedEmployees = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredEmployees.value.slice(start, end);
});

const totalPages = computed(() => {
  return Math.ceil(filteredEmployees.value.length / itemsPerPage);
});

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const pages = [];

  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push("...");
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(total);
    }
  }

  return pages.filter(
    (page) => page !== "..." || pages.indexOf(page) === pages.lastIndexOf(page)
  );
});

const activeEmployees = computed(() => {
  if (!employees.value || !Array.isArray(employees.value)) return 0;
  return employees.value.filter((emp) => emp.is_active).length;
});

const inactiveEmployees = computed(() => {
  if (!employees.value || !Array.isArray(employees.value)) return 0;
  return employees.value.filter((emp) => !emp.is_active).length;
});

// Methods
const fetchDepartments = async () => {
  try {
    console.log('🏢 Fetching departments...');
    const response = await adminService.getDepartments();
    departmentsList.value = response || [];
    console.log('✅ Departments loaded:', departmentsList.value);
  } catch (error) {
    console.error("Error fetching departments:", error);
    toast.error("Failed to load departments");
  }
};

const fetchEmployees = async () => {
  try {
    loading.value = true;
    const response = await adminService.getAllEmployees();
    employees.value = response.employees || [];
  } catch (error) {
    console.error("Error fetching employees:", error);
    toast.error("Failed to load employees");
  } finally {
    loading.value = false;
  }
};

const fetchManagers = async () => {
  try {
    managersList.value = await adminService.getManagerCandidates();
  } catch (error) {
    console.error("Error fetching manager candidates:", error);
    managersList.value = [];
  }
};

const openAddModal = () => {
  resetForm();
  clearErrors();
  showAddModal.value = true;
};

const editEmployee = (employee) => {
  selectedEmployee.value = employee;
  
  // Find the department ID for the employee's department name
  const employeeDept = departmentsList.value.find(dept => dept.name === employee.department);
  const departmentId = employeeDept ? employeeDept.id : "";
  
  console.log('🔧 Editing employee:', employee.name, 'Department:', employee.department, 'ID:', departmentId);
  
  form.value = {
    employee_id: employee.employee_id,
    name: employee.name,
    email: employee.email,
    phone: employee.phone || "",
    department: departmentId,
    position: employee.position,
    salary: employee.salary || "",
    hire_date: employee.hire_date ? employee.hire_date.split("T")[0] : "",
    work_schedule: employee.work_schedule || "office",
    manager_id: employee.manager_id || "",
    password: "",
    confirmPassword: "",
  };
  clearErrors();
  showViewModal.value = false;
  showEditModal.value = true;
};

const viewEmployee = (employee) => {
  selectedEmployee.value = employee;
  showViewModal.value = true;
};

const closeModal = () => {
  // Only close if not currently saving
  if (saving.value) {
    console.warn('⚠️ Cannot close modal while saving');
    return;
  }

  showAddModal.value = false;
  showEditModal.value = false;
  resetForm();
  clearErrors();
  backdropMouseDownTarget.value = null;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedEmployee.value = null;
};

// 🔒 MODAL STABILITY: Enhanced backdrop click handler
// Only closes modal if mousedown and click both happen on the backdrop (not the modal content)
const handleBackdropMouseDown = (event) => {
  // Record where the mousedown started
  backdropMouseDownTarget.value = event.target;
};

const handleBackdropClick = (event) => {
  // Only close if:
  // 1. The click is on the backdrop itself (not a child element)
  // 2. The mousedown also started on the backdrop
  // 3. The modal content wasn't involved in the click
  const clickedOnBackdrop = event.target === event.currentTarget;
  const mouseDownWasOnBackdrop = backdropMouseDownTarget.value === event.currentTarget;

  if (clickedOnBackdrop && mouseDownWasOnBackdrop) {
    console.log('🔒 Backdrop clicked intentionally, closing modal');
    closeModal();
  } else {
    console.log('🔒 Click ignored - interaction was with modal content');
  }

  // Reset tracker
  backdropMouseDownTarget.value = null;
};

// 🔒 KEYBOARD SUPPORT: ESC key to close modal
const handleKeyDown = (event) => {
  if (event.key === 'Escape' && (showAddModal.value || showEditModal.value) && !saving.value) {
    console.log('🔒 ESC key pressed, closing modal');
    closeModal();
  }
};

const resetForm = () => {
  form.value = {
    employee_id: "",
    name: "",
    email: "",
    phone: "",
    department: "",
    position: "",
    salary: "",
    hire_date: "",
    work_schedule: "office",
    manager_id: "",
    password: "",
    confirmPassword: "",
  };
  selectedEmployee.value = null;
};

const saveEmployee = async () => {
  const isEdit = showEditModal.value;

  if (!validateForm(form.value, isEdit)) {
    toast.error("Please fix the validation errors");
    return;
  }

  try {
    saving.value = true;
    setSubmitting(true);

    // Prepare data for submission
    const employeeData = { ...form.value };
    delete employeeData.confirmPassword;

    // Remove empty salary if not provided
    if (!employeeData.salary) {
      delete employeeData.salary;
    }

    if (isEdit) {
      // Remove password if not provided for edit
      if (!employeeData.password) {
        delete employeeData.password;
      }

      await adminService.updateEmployee(
        selectedEmployee.value.id,
        employeeData
      );
      toast.success("Employee updated successfully");
    } else {
      await adminService.createEmployee(employeeData);
      toast.success("Employee created successfully");
    }

    closeModal();
    await fetchEmployees();
    fetchManagers();
  } catch (error) {
    console.error("Error saving employee:", error);
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error(
        isEdit ? "Failed to update employee" : "Failed to create employee"
      );
    }
  } finally {
    saving.value = false;
    setSubmitting(false);
  }
};

const toggleEmployeeStatus = async (employee) => {
  try {
    await adminService.toggleEmployeeStatus(employee.id);
    toast.success(
      `Employee ${
        employee.is_active ? "deactivated" : "activated"
      } successfully`
    );
    await fetchEmployees();
  } catch (error) {
    console.error("Error toggling employee status:", error);
    toast.error("Failed to update employee status");
  }
};

const resetEmployeePassword = async (employee) => {
  if (
    !confirm(
      `Are you sure you want to reset the password for ${employee.name}?`
    )
  ) {
    return;
  }

  try {
    const response = await adminService.resetEmployeePassword(employee.id);
    toast.success(
      `Password reset successfully. New password: ${response.data.newPassword}`
    );
  } catch (error) {
    console.error("Error resetting password:", error);
    toast.error("Failed to reset password");
  }
};

const wipeEmployee = async (employee) => {
  if (
    !confirm(
      `WARNING: This will permanently delete ALL data for "${employee.name}" — attendance, leaves, payroll, and their account. This cannot be undone.\n\nType OK to confirm.`
    )
  ) {
    return;
  }

  try {
    await adminService.wipeEmployee(employee.id);
    toast.success(`All data for "${employee.name}" has been permanently deleted`);
    await fetchEmployees();
  } catch (error) {
    console.error("Error wiping employee:", error);
    toast.error("Failed to wipe employee data");
  }
};

const exportEmployees = async () => {
  try {
    await exportEmployeesToExcel(employees.value, "employees");
    toast.success("Employees exported successfully");
  } catch (error) {
    console.error("Error exporting employees:", error);
    toast.error("Failed to export employees");
  }
};

const handleImport = () => {
  fileInput.value?.click();
};

const handleFileSelect = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    const importedData = await parseExcelFile(file);
    console.log("Imported data:", importedData);

    // Process imported data and create employees
    // This would need validation and confirmation dialog in production
    toast.success(`Found ${importedData.length} employees in the file`);

    // Reset file input
    event.target.value = "";
  } catch (error) {
    console.error("Error importing file:", error);
    toast.error("Failed to import employees from file");
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString();
};


// 🔒 MODAL AUTOFOCUS: Focus first input when modal opens
watch([showAddModal, showEditModal], async ([isAddOpen, isEditOpen]) => {
  if (isAddOpen || isEditOpen) {
    await nextTick();
    // Focus the first input field
    const firstInput = document.querySelector('input[type="text"]:not([disabled])');
    if (firstInput) {
      firstInput.focus();
      console.log('✨ Auto-focused first input field');
    }
  }
});

// Lifecycle
onMounted(async () => {
  loadCurrency();
  // Fetch departments first, then employees
  await fetchDepartments();
  await fetchEmployees();
  fetchManagers();

  // Add keyboard event listener for ESC key
  window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  // Clean up keyboard event listener
  window.removeEventListener('keydown', handleKeyDown);
});
</script>

<style scoped>
/* 🎨 Modal animations - Enhanced slide-in with bounce */
@keyframes modal-slide-in {
  0% {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
  }
  60% {
    opacity: 1;
    transform: translateY(5px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-modal-slide-in {
  animation: modal-slide-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* 🎨 Backdrop blur animation */
@keyframes backdrop-fade-in {
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(4px);
  }
}

/* 🎨 Smooth transitions for all form elements */
input,
select,
textarea,
button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 🎨 Enhanced input focus effects with glow */
input:focus,
select:focus,
textarea:focus {
  transform: translateY(-1px);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1),
              0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* 🎨 Error state with subtle shake */
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

input.border-red-300,
select.border-red-300 {
  animation: shake 0.3s ease-in-out;
}

/* 🎨 Enhanced button hover effects with scale */
button:not(:disabled) {
  cursor: pointer;
}

button:not(:disabled):hover {
  filter: brightness(1.05);
}

button:not(:disabled):active {
  transform: scale(0.98);
}

/* 🎨 Gradient button shimmer effect */
@keyframes shimmer {
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
}

button[type="submit"]:not(:disabled):hover {
  background-size: 200% auto;
  animation: shimmer 2s linear infinite;
}

/* 🎨 Section headers with subtle animation */
h4 {
  position: relative;
}

h4::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, currentColor, transparent);
  opacity: 0.3;
}

/* 🎨 Icon containers with hover effect */
.bg-blue-100, .bg-green-100, .bg-purple-100, .bg-amber-100 {
  transition: all 0.2s ease;
}

/* 🎨 Keyboard shortcut badge styling */
kbd {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1),
              inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  font-family: ui-monospace, monospace;
}

/* 🎨 Better scrollbar styling for modal */
.overflow-y-auto::-webkit-scrollbar {
  width: 10px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
  border-radius: 10px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, #d1d5db, #9ca3af);
  border-radius: 10px;
  border: 2px solid #f9fafb;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to bottom, #9ca3af, #6b7280);
}

/* 🎨 Form sections with smooth reveal */
@keyframes section-reveal {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

form > div {
  animation: section-reveal 0.4s ease-out backwards;
}

form > div:nth-child(1) { animation-delay: 0.05s; }
form > div:nth-child(2) { animation-delay: 0.1s; }
form > div:nth-child(3) { animation-delay: 0.15s; }
form > div:nth-child(4) { animation-delay: 0.2s; }
form > div:nth-child(5) { animation-delay: 0.25s; }

/* 🎨 Required field asterisk pulse */
@keyframes pulse-red {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.text-red-500 {
  animation: pulse-red 2s ease-in-out infinite;
}

/* 🎨 Modal header gradient animation */
@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.bg-gradient-to-r {
  background-size: 200% auto;
  animation: gradient-shift 10s ease infinite;
}

/* 🎨 Help text icon bounce */
@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

.bg-blue-100:has(+ * .text-blue-600) {
  animation: bounce-subtle 2s ease-in-out infinite;
}

/* 🎨 Placeholder improvements */
::placeholder {
  color: #9ca3af;
  font-weight: 400;
  opacity: 0.6;
}

input:focus::placeholder,
select:focus::placeholder {
  opacity: 0.4;
}

/* 🎨 Responsive adjustments */
@media (max-width: 640px) {
  .animate-modal-slide-in {
    animation: modal-slide-in 0.3s ease-out;
  }

  form > div {
    animation: none;
  }
}

/* 🎨 Selection styling */
::selection {
  background-color: rgba(59, 130, 246, 0.2);
  color: inherit;
}

/* 🎨 Focus visible for accessibility */
*:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
</style>
